import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, authedUrl } from "@/lib/api";
import { useChatSocket } from "@/lib/socket";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Search, MoreVertical, Reply, Pencil, Trash2, X, Download, Check, CheckCheck, Volume2, VolumeX, Phone, PhoneOff, PhoneMissed, Forward, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import Linkify from "@/components/chat/Linkify";
import ImageLightbox from "@/components/chat/ImageLightbox";
import ChatComposer, { DropOverlay, UploadingList } from "@/components/chat/ChatComposer";
import AnimatedEmoji from "@/components/chat/AnimatedEmoji";

/** Splits a string into Unicode graphemes (best-effort). */
function splitGraphemes(text) {
  try {
    const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    return Array.from(seg.segment(text), (s) => s.segment);
  } catch {
    return [...text];
  }
}

/** Detects if a message is nothing but emojis (with whitespace).
 *  Uses Unicode property escapes so both classic and modern emojis are matched.
 *  Returns { isEmoji: boolean, count: number } — count is the visible emoji count. */
function analyzeEmojiOnly(text) {
  if (!text) return { isEmoji: false, count: 0 };
  const stripped = text.replace(/\s+/g, "");
  if (!stripped) return { isEmoji: false, count: 0 };
  let emojiRe;
  try {
    emojiRe = /^(?:\p{Extended_Pictographic}(?:️)?(?:‍\p{Extended_Pictographic}(?:️)?)*|\p{Emoji_Component})+$/u;
  } catch {
    return { isEmoji: false, count: 0 };
  }
  if (!emojiRe.test(stripped)) return { isEmoji: false, count: 0 };
  let count = 0;
  try {
    const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    for (const _ of seg.segment(stripped)) count++;
  } catch {
    count = [...stripped].length;
  }
  return { isEmoji: count > 0 && count <= 6, count };
}

export function CallBubble({ msg, mine }) {
  const c = msg.call || {};
  const outcome = c.outcome || "completed";
  const dur = c.duration_seconds || 0;
  const mm = String(Math.floor(dur / 60)).padStart(2, "0");
  const ss = String(dur % 60).padStart(2, "0");
  const label =
    outcome === "missed" ? "Missed voice call" :
    outcome === "declined" ? "Declined" :
    outcome === "cancelled" ? "Cancelled" :
    `Voice call · ${mm}:${ss}`;
  const Icon = outcome === "missed" ? PhoneMissed : outcome === "completed" ? Phone : PhoneOff;
  return (
    <div className="flex items-center gap-2 py-0.5">
      <Icon className={`w-4 h-4 ${outcome === "missed" ? "text-red-500" : mine ? "opacity-80" : "opacity-70"}`} />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function AttachmentView({ att, onImageClick }) {
  const url = authedUrl(att.url);
  if (att.kind === "image") {
    return (
      <img
        src={url}
        alt={att.filename}
        loading="lazy"
        onClick={() => onImageClick?.({ url, rawUrl: att.url, filename: att.filename, mime: att.mime })}
        className="max-w-xs max-h-64 rounded-lg object-contain cursor-zoom-in hover:opacity-90 transition"
      />
    );
  }
  if (att.kind === "video") return <video src={url} controls className="max-w-xs max-h-64 rounded-lg" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" download={att.filename} className="flex items-center gap-2 px-3 py-2 rounded-lg hairline hover:bg-background/50">
      <Download className="w-4 h-4" />
      <div className="min-w-0"><div className="text-sm truncate max-w-[200px]">{att.filename}</div><div className="text-[10px] opacity-70">{Math.round(att.size / 1024)} KB · {att.mime}</div></div>
    </a>
  );
}

function ReadReceipt({ readByOther }) {
  const cls = readByOther ? "text-[hsl(var(--check-read))]" : "text-[hsl(var(--check-sent))]";
  const Icon = readByOther ? CheckCheck : Check;
  return <Icon className={`w-3.5 h-3.5 ${cls}`} data-testid={readByOther ? "read-double" : "sent-single"} />;
}

// --- Day-separator helper (WhatsApp style) ---
function _dayKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabelIfNew(msg, prev) {
  if (!msg?.created_at) return null;
  if (prev && _dayKey(prev.created_at) === _dayKey(msg.created_at)) return null;
  const d = new Date(msg.created_at);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (_dayKey(d) === _dayKey(today)) return "Today";
  if (_dayKey(d) === _dayKey(yesterday)) return "Yesterday";
  const diffDays = Math.floor((_dayKey(today) === _dayKey(d) ? 0 : (today - d) / 86400000));
  if (diffDays < 7 && d < today) {
    return d.toLocaleDateString(undefined, { weekday: "long" });
  }
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const initialsOf = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

// --- Forward dialog: pick one or more recipients and forward a message. ---
function ForwardDialog({ message, contacts, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);

  const filtered = contacts.filter((c) => !q || c.full_name.toLowerCase().includes(q.toLowerCase()));

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const send = async () => {
    if (selected.size === 0) return toast.error("Pick at least one contact");
    setSending(true);
    try {
      await api.post(`/chat/messages/${message.id}/forward`, { recipient_ids: Array.from(selected) });
      toast.success(`Forwarded to ${selected.size} chat${selected.size > 1 ? "s" : ""}`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forward failed");
    } finally {
      setSending(false);
    }
  };

  const preview = message.content || (message.attachment ? `📎 ${message.attachment.filename}` : "message");

  return (
    <Dialog open={!!message} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="fwd-dialog">
        <DialogHeader>
          <DialogTitle>Forward message</DialogTitle>
          <DialogDescription className="truncate">"{preview}"</DialogDescription>
        </DialogHeader>
        <Input
          data-testid="fwd-search"
          placeholder="Search contacts…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9"
        />
        <div className="max-h-72 overflow-y-auto scroll-thin space-y-1">
          {filtered.map((e) => {
            const checked = selected.has(e.id);
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => toggle(e.id)}
                data-testid={`fwd-pick-${e.id}`}
                className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-sm transition-colors ${checked ? "bg-primary/15" : "hover:bg-accent"}`}
              >
                <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(e.avatar_url)} /><AvatarFallback className="text-[9px]">{initialsOf(e.full_name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{e.full_name}</div>
                  {e.designation && <div className="text-[10px] text-muted-foreground truncate">{e.designation}</div>}
                </div>
                {checked && <Check className="w-4 h-4 text-primary" />}
              </button>
            );
          })}
          {filtered.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">No contacts found</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button data-testid="fwd-send" onClick={send} disabled={sending || selected.size === 0}>
            <Forward className="w-4 h-4 mr-1" />
            {sending ? "Forwarding…" : `Forward${selected.size ? ` (${selected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One conversation thread + composer. Shared by the 1:1 Chat page and the
 * project Discussion tab (isGroup).
 *
 * Props:
 *  - conversation: conversation object ({id, ...})
 *  - user: current user
 *  - contacts: people that can be forwarded to (also used for sender names)
 *  - members: people who can be @mentioned / shown by name in a group
 *  - isGroup: show sender names + enable @mentions
 *  - headerLeft: ({ typingFrom, onlineUsers }) => node — left side of the header
 *  - headerExtra: node placed before the search / mute buttons (e.g. call button)
 *  - hideHeader: render without a header bar
 *  - onActivity: called after any message event (parent refreshes its list)
 *  - onPresence: (event) => void — forwarded presence events (1:1 pages)
 */
export default function ChatPane({
  conversation, user, contacts = [], members = [], isGroup = false,
  headerLeft, headerExtra, hideHeader = false, onActivity, onSocketEvent, className = "",
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [typingFrom, setTypingFrom] = useState(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("chat_muted") === "1");
  const [lightbox, setLightbox] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [forwarding, setForwarding] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const scrollRef = useRef(null);
  const msgRefs = useRef({});
  const dragCounter = useRef(0);
  const typingTimeout = useRef(null);
  const convRef = useRef(conversation);
  const onActivityRef = useRef(onActivity);
  const onSocketEventRef = useRef(onSocketEvent);
  useEffect(() => { convRef.current = conversation; }, [conversation]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);
  useEffect(() => { onSocketEventRef.current = onSocketEvent; }, [onSocketEvent]);

  useEffect(() => {
    localStorage.setItem("chat_muted", muted ? "1" : "0");
    window.dispatchEvent(new Event("chat-mute-changed"));
  }, [muted]);

  const cid = conversation?.id;

  const nameById = useMemo(() => {
    const map = {};
    [...contacts, ...members].forEach((p) => { if (p?.id) map[p.id] = p; });
    if (user) map[user.id] = { id: user.id, full_name: user.full_name, avatar_url: user.avatar_url };
    return map;
  }, [contacts, members, user]);

  const mentionables = useMemo(
    () => (isGroup ? members.filter((m) => m.id !== user.id) : []),
    [isGroup, members, user]
  );

  const markRead = useCallback(() => {
    if (!cid) return;
    api.post(`/chat/conversations/${cid}/read`)
      .then(() => window.dispatchEvent(new Event("chat-unread-changed")))
      .catch(() => {});
  }, [cid]);

  const loadMessages = useCallback(async () => {
    if (!cid) return;
    const { data } = await api.get(`/chat/conversations/${cid}/messages`);
    setMessages(data);
    markRead();
    onActivityRef.current?.();
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
  }, [cid, markRead]);

  useEffect(() => {
    setMessages([]); setReplyTo(null); setEditing(null); setInput("");
    setSearching(false); setSearchQ("");
    loadMessages().catch(() => {});
  }, [cid, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const { send } = useChatSocket({
    onEvent: (ev) => {
      onSocketEventRef.current?.(ev);
      const cur = convRef.current;
      if (ev.event === "message.new") {
        if (cur && ev.message.conversation_id === cur.id) {
          setMessages((m) => m.some((x) => x.id === ev.message.id) ? m : [...m, ev.message]);
          if (ev.message.sender_id !== user.id) markRead();
        }
        onActivityRef.current?.();
      } else if (ev.event === "message.edit") {
        setMessages((m) => m.map((x) => x.id === ev.message.id ? ev.message : x));
      } else if (ev.event === "message.delete") {
        setMessages((m) => m.map((x) => x.id === ev.message_id ? { ...x, deleted_for_everyone: true, content: "" } : x));
      } else if (ev.event === "typing") {
        if (cur && ev.conversation_id === cur.id) {
          setTypingFrom(ev.typing ? ev.user_id : null);
          if (ev.typing) {
            clearTimeout(typingTimeout.current);
            typingTimeout.current = setTimeout(() => setTypingFrom(null), 3500);
          }
        }
      } else if (ev.event === "presence") {
        setOnlineUsers((s) => { const n = new Set(s); ev.online ? n.add(ev.user_id) : n.delete(ev.user_id); return n; });
      } else if (ev.event === "presence.snapshot") {
        setOnlineUsers(new Set(ev.online_users));
      }
    },
  });

  const runSearch = async () => {
    if (!cid || !searchQ) return setSearching(false);
    const { data } = await api.get(`/chat/conversations/${cid}/messages?q=${encodeURIComponent(searchQ)}`);
    setMessages(data);
    setSearching(true);
  };

  /** Employee ids tagged with @Full Name inside the text. */
  const extractMentions = (text) => {
    if (!isGroup) return [];
    const lower = text.toLowerCase();
    return mentionables.filter((m) => lower.includes(`@${m.full_name.toLowerCase()}`)).map((m) => m.id);
  };

  const sendText = async () => {
    if (!input.trim() || !cid) return;
    if (editing) {
      try { await api.patch(`/chat/messages/${editing.id}`, { content: input }); setEditing(null); setInput(""); }
      catch { toast.error("Edit failed"); }
      return;
    }
    try {
      const { data } = await api.post("/chat/messages", {
        conversation_id: cid, content: input, reply_to: replyTo?.id, mentions: extractMentions(input),
      });
      setMessages((m) => m.some((x) => x.id === data.id) ? m : [...m, data]);
      setInput(""); setReplyTo(null);
      onActivityRef.current?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const onFile = async (file) => {
    if (!cid || !file) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploads((u) => [...u, { id, file, status: "uploading" }]);
    const fd = new FormData();
    fd.append("conversation_id", cid);
    fd.append("file", file);
    if (replyTo) fd.append("reply_to", replyTo.id);
    try {
      await api.post("/chat/attachments", fd);
      setUploads((u) => u.filter((x) => x.id !== id));
      setReplyTo(null);
    } catch (e) {
      setUploads((u) => u.map((x) => x.id === id ? { ...x, status: "error" } : x));
      toast.error(e?.response?.data?.detail || `Upload failed: ${file.name}`);
    }
  };

  const onFiles = (files) => { Array.from(files || []).forEach((f) => onFile(f)); };

  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCounter.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) onFiles(files);
  };

  const scrollToMessage = useCallback((mid) => {
    const el = msgRefs.current[mid];
    if (!el) {
      toast.info("Original message not in current view");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(mid);
    setTimeout(() => setHighlightId((cur) => (cur === mid ? null : cur)), 1800);
  }, []);

  const del = async (m, forEveryone) => {
    if (!window.confirm(forEveryone ? "Delete for everyone?" : "Delete for you?")) return;
    try { await api.delete(`/chat/messages/${m.id}?for_everyone=${forEveryone}`); if (!forEveryone) setMessages((prev) => prev.filter((x) => x.id !== m.id)); }
    catch { toast.error("Failed"); }
  };

  const onTyping = () => { if (cid) send({ event: "typing", conversation_id: cid, typing: true }); };

  const msgById = useMemo(() => Object.fromEntries(messages.map((m) => [m.id, m])), [messages]);
  const mentionNames = useMemo(() => mentionables.map((m) => m.full_name), [mentionables]);

  const typingLabel = typingFrom
    ? (isGroup ? `${nameById[typingFrom]?.full_name || "Someone"} is typing…` : "typing…")
    : null;

  return (
    <section
      className={`flex flex-col min-h-0 min-w-0 chat-canvas relative ${className}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <DropOverlay visible={dragOver} />
      {!hideHeader && (
        <div className="p-3 border-b border-border/60 flex items-center gap-3 bg-background/80 backdrop-blur-sm min-w-0">
          <div className="flex-1 min-w-0 flex items-center gap-3">
            {headerLeft?.({ typingFrom, typingLabel, onlineUsers })}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Input placeholder="Search messages" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }} className="h-8 w-40 hidden sm:block" data-testid="msg-search" />
            <Button data-testid="msg-search-btn" variant="ghost" size="icon" onClick={runSearch}><Search className="w-4 h-4" /></Button>
            {searching && <Button data-testid="msg-search-clear" variant="ghost" size="sm" onClick={() => { setSearching(false); setSearchQ(""); loadMessages(); }}>Clear</Button>}
            {headerExtra}
            <Button data-testid="chat-mute-toggle" variant="ghost" size="icon" onClick={() => setMuted((v) => !v)} title={muted ? "Unmute notifications" : "Mute notifications"}>
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden scroll-thin p-4 space-y-1.5">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
            <MessageSquarePlus className="w-8 h-8 opacity-40" />
            <div>{isGroup ? "No messages yet — start the discussion." : "No messages yet."}</div>
          </div>
        )}
        {messages.map((m, i) => {
          const mine = m.sender_id === user.id;
          const rt = m.reply_to ? msgById[m.reply_to] : null;
          const isDeleted = m.deleted_for_everyone;
          const readByOther = mine && (m.read_by || []).some((uid) => uid !== user.id);
          const isHighlighted = highlightId === m.id;
          const dayLabel = dayLabelIfNew(m, messages[i - 1]);
          const sender = nameById[m.sender_id];
          const showSender = isGroup && !mine && m.kind !== "call" && messages[i - 1]?.sender_id !== m.sender_id;
          const tagsMe = (m.mentions || []).includes(user.id);
          const renderText = (text) => (
            <MentionText text={text} names={mentionNames} className="text-sm whitespace-pre-wrap leading-snug break-words [overflow-wrap:anywhere]" />
          );
          return (
            <React.Fragment key={m.id}>
              {dayLabel && (
                <div className="flex justify-center my-3" data-testid={`day-sep-${dayLabel}`}>
                  <span className="text-[11px] font-medium px-3 py-1 rounded-full bg-background/70 text-muted-foreground border border-border/60 backdrop-blur-sm shadow-sm">
                    {dayLabel}
                  </span>
                </div>
              )}
              <div
                data-testid={`msg-${m.id}`}
                ref={(el) => { if (el) msgRefs.current[m.id] = el; else delete msgRefs.current[m.id]; }}
                className={`flex w-full min-w-0 gap-2 ${mine ? "justify-end" : "justify-start"} group transition-colors ${isHighlighted ? "bg-primary/10 rounded-lg" : ""}`}
              >
                {isGroup && !mine && (
                  <div className="w-7 shrink-0">
                    {showSender && (
                      <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(sender?.avatar_url)} /><AvatarFallback className="text-[9px]">{initialsOf(sender?.full_name)}</AvatarFallback></Avatar>
                    )}
                  </div>
                )}
                <div className={`min-w-0 max-w-[min(70%,560px)] rounded-lg px-2.5 py-1.5 shadow-sm msg-pop ${mine ? "chat-bubble-mine rounded-tr-sm" : "chat-bubble-other rounded-tl-sm"} ${tagsMe && !mine ? "ring-1 ring-primary/50" : ""}`}>
                  {showSender && (
                    <div className="text-[11px] font-semibold text-primary mb-0.5" data-testid={`sender-${m.id}`}>{sender?.full_name || "Someone"}</div>
                  )}
                  {m.forwarded && !isDeleted && (
                    <div className="flex items-center gap-1 text-[10px] italic opacity-60 mb-0.5" data-testid={`fwd-tag-${m.id}`}>
                      <Forward className="w-3 h-3" />Forwarded
                    </div>
                  )}
                  {rt && (
                    <button
                      type="button"
                      onClick={() => scrollToMessage(rt.id)}
                      data-testid={`reply-jump-${m.id}`}
                      className="w-full text-left text-[10px] px-2 py-1 mb-1 rounded border-l-2 border-current/40 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition"
                    >
                      <div className="opacity-70">Replying to{isGroup && nameById[rt.sender_id] ? ` ${nameById[rt.sender_id].full_name}` : ""}</div>
                      <div className="truncate">{rt.content || (rt.attachment ? `📎 ${rt.attachment.filename}` : "message")}</div>
                    </button>
                  )}
                  {isDeleted ? (
                    <div className="italic opacity-60 text-sm">This message was deleted</div>
                  ) : m.kind === "call" ? (
                    <CallBubble msg={m} mine={mine} />
                  ) : m.attachment ? (
                    <div className="space-y-1">
                      <AttachmentView att={m.attachment} onImageClick={setLightbox} />
                      {m.content && renderText(m.content)}
                    </div>
                  ) : (() => {
                    const eo = analyzeEmojiOnly(m.content);
                    if (eo.isEmoji) {
                      const size = eo.count === 1 ? 96 : eo.count === 2 ? 72 : eo.count === 3 ? 60 : 48;
                      const graphemes = splitGraphemes((m.content || "").replace(/\s+/g, ""));
                      return (
                        <div className="py-1 flex items-center gap-1 flex-wrap" data-testid={`emoji-jumbo-${m.id}`}>
                          {graphemes.map((g, idx) => (
                            <AnimatedEmoji key={idx} emoji={g} size={size} />
                          ))}
                        </div>
                      );
                    }
                    return renderText(m.content);
                  })()}
                  <div className={`text-[10px] mt-0.5 opacity-70 flex items-center gap-1 ${mine ? "justify-end" : ""}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {m.edited && <span>· edited</span>}
                    {mine && !isDeleted && <ReadReceipt readByOther={readByOther} />}
                    {mine && !isDeleted && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="opacity-0 group-hover:opacity-100"><MoreVertical className="w-3 h-3" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setReplyTo(m)}><Reply className="w-3 h-3 mr-2" />Reply</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`fwd-mine-${m.id}`} onClick={() => setForwarding(m)}><Forward className="w-3 h-3 mr-2" />Forward</DropdownMenuItem>
                          {m.kind === "text" && <DropdownMenuItem onClick={() => { setEditing(m); setInput(m.content); }}><Pencil className="w-3 h-3 mr-2" />Edit</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => del(m, false)}><Trash2 className="w-3 h-3 mr-2" />Delete for me</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => del(m, true)} className="text-red-500"><Trash2 className="w-3 h-3 mr-2" />Delete for everyone</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {!mine && !isDeleted && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="opacity-0 group-hover:opacity-100"><MoreVertical className="w-3 h-3" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setReplyTo(m)}><Reply className="w-3 h-3 mr-2" />Reply</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`fwd-other-${m.id}`} onClick={() => setForwarding(m)}><Forward className="w-3 h-3 mr-2" />Forward</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {typingLabel && isGroup && (
        <div className="px-4 pb-1 text-[11px] text-muted-foreground" data-testid="group-typing">{typingLabel}</div>
      )}

      {replyTo && (
        <div className="px-3 py-2 border-t border-border/60 flex items-center gap-2 text-xs bg-accent/50">
          <Reply className="w-3 h-3" />
          <div className="flex-1 truncate">Replying to: {replyTo.content || (replyTo.attachment ? `📎 ${replyTo.attachment.filename}` : "message")}</div>
          <button onClick={() => setReplyTo(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      {editing && (
        <div className="px-3 py-2 border-t border-border/60 flex items-center gap-2 text-xs bg-primary/10">
          <Pencil className="w-3 h-3" />
          <div className="flex-1 truncate">Editing message</div>
          <button onClick={() => { setEditing(null); setInput(""); }}><X className="w-3 h-3" /></button>
        </div>
      )}

      <UploadingList items={uploads} onRemove={(id) => setUploads((u) => u.filter((x) => x.id !== id))} />

      <ChatComposer
        value={input}
        onChange={setInput}
        onSend={sendText}
        onFiles={onFiles}
        onTyping={onTyping}
        mentionables={mentionables}
        placeholder={isGroup ? "Message the team… use @ to tag someone" : undefined}
      />

      <ImageLightbox img={lightbox} onClose={() => setLightbox(null)} />
      {forwarding && (
        <ForwardDialog
          message={forwarding}
          contacts={contacts.filter((e) => e.id !== user?.id && e.id !== "tappy-bot")}
          onClose={() => setForwarding(null)}
        />
      )}
    </section>
  );
}

/** Message text with `@Full Name` tags highlighted, URLs still linkified. */
function MentionText({ text, names, className }) {
  if (!text || !names.length) return <Linkify text={text} className={className} />;
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(@(?:${escaped.join("|")}))`, "gi");
  const parts = text.split(re);
  return (
    <span className={className}>
      {parts.map((part, i) => (
        i % 2 === 1
          ? <span key={i} className="font-semibold text-primary bg-primary/10 rounded px-0.5" data-testid="mention-tag">{part}</span>
          : <Linkify key={i} text={part} />
      ))}
    </span>
  );
}
